import {IService, IServicesByPatient, ServiceState} from '../../models/service';
import {UpdatePatientInformationRequestDTO} from '../../dtos/patient';
import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {IPatient, PatientAction, PatientState} from '../../models/patient';
import {GenderEnum} from '../../models/user';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {AuthActions, IUser} from '../../models/auth';
import {IClinicalObjective} from '../../models/clinic';
import {IIndicatorsPatientConsultation} from '../../models/measurement';
import {Loader} from '../../models/loader/loader.redux';
import {StorageService} from '../../services/storage/storage.service';
import {Logger} from '../../class/helper/logger.class';
import {cloneDeep} from 'lodash';
import {RolesEnum} from '../../enums/role';
import {UserService} from '../../services/user/user.service';

@Component({
  selector: 'app-consultation',
  templateUrl: './consultation.component.html',
  styleUrls: ['./consultation.component.scss'],
  animations: [fadeInAnimation],
})
export class ConsultationComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() userType: 'expert' | 'patient';
  @Input() showActions = true;
  @Input() horizontalMode = false;
  @Output() openAppointmentModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() openNoteModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changeServiceExpert: EventEmitter<string> = new EventEmitter<string>();
  patient$: Observable<IPatient> = this.store.select<IPatient>(PatientState.getSelectedPatient);
  service$: Observable<IService> = this.store.select<IService>(ServiceState.getActive);
  servicesByPatient$: Observable<IServicesByPatient[]> = this.store.select<IServicesByPatient[]>(ServiceState.getServicesByPatient);
  indicatorsPatient$: Observable<IIndicatorsPatientConsultation> =
    this.store.select<IIndicatorsPatientConsultation>(PatientState.getPatientSelectedInformation);
  subscriptionPatient: Subscription;
  subscriptionService: Subscription;
  user: IUser;
  genderEnum = GenderEnum;
  indicators: IIndicatorsPatientConsultation;
  appointmentId: string;
  patientId: string;
  clinicId: string;
  patientClinicalObjectives: IClinicalObjective[] = [];
  service: any;
  serviceName: string;
  servicePatientSubscription: Subscription;
  addPatientGoal: boolean;
  indicatorsPatientSubscription: Subscription;
  changePatientName: boolean;
  userLogged: IUser;
  userAuthRole: RolesEnum | undefined;
  roles = RolesEnum;
  constructor(
    public utilitiesServices: UtilitiesService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private storageService: StorageService,
    private userService: UserService,
  ) {

    /**
     * Detecta cuando cambia los servicios del paciente.
     */
    this.servicePatientSubscription = this.servicesByPatient$.subscribe(
      (service) => {
        if (!(service && service[0])) return;
        this.serviceName = service[0].expertId;
        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (queryParams.expert) this.serviceName = queryParams.expert;
      }
    );

    /**
     * Detecta cuando cambian los indicadores de peso, altura, imc del paciente.
     */
    this.indicatorsPatientSubscription = this.indicatorsPatient$.subscribe(
      (data) => {
        this.indicators = cloneDeep(data);
      }
    );
  }

  ngOnInit(): void {
    this.userLogged = this.storageService.getLocalItem<{ token: string, user: IUser }>('user')?.user;
    this.userAuthRole = this.userService.getRoleByUser();

    /**
     * Detecta cuando cambia la información del paciente.
     */
    this.subscriptionPatient = this.patient$.subscribe((patient) => {
      if (patient && patient.user) {
        this.user = cloneDeep(patient.user);
      }
    });

    /**
     * Detecta el servicio activo del paciente.
     */
    this.subscriptionService = this.service$.subscribe(async (service) => {
      if (service && service.servicePatients && service.servicePatients[0]) {
        this.service = service;
        this.setClinicalObjectives();
      } else {
        this.service = null;
      }
    });
    this.getParams();
  }

  async ngAfterViewInit(): Promise<void> {
  }

  /**
   * Setea los objetivos clínicos del paciente
   */
  setClinicalObjectives(): void {
    if (!this.service) return;
    this.patientClinicalObjectives = this.service.servicePatients[0].clinicObjectives;
  }

  /**
   * Obtiene los parametros de la url
   */
  getParams(): void {
    const params: Params = this.utilitiesServices.getAllParams(
      this.activatedRoute
    );
    this.appointmentId = params.appointmentId;
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  /**
   * Emite al componente padre cuando se quiere crear una nueva cita.
   */
  onEmitViewModalCreateAppointment(): void {
    this.openAppointmentModal.emit(true);
  }

  /**
   * Emite al componente padre cuando se quiere crear una nueva nota.
   */
  onEmitViewModalCreateNote(): void {
    this.openNoteModal.emit(true);
  }

  /**
   * Emite al componente padre cuando se quiere cambiar el servicio por el que se tiene con otro experto.
   * @param $event Evento
   */
  onEmitChangeServiceExpert($event): void {
    this.changeServiceExpert.emit($event);
  }

  /**
   * Guarda la meta del paciente.
   */
  async onSavePatientGoal(): Promise<void> {
    try {
      const [servicePatient] = this.service.servicePatients ?? [];
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Actualizando meta del paciente ...'))));
      await firstValueFrom(this.store
        .dispatch(
          new PatientAction.PatchInformation(
            servicePatient.patientId,
            this.service._id,
            { patientGoal: this.indicators.servicePatient.patientGoal }
          )));
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    } finally {
      this.addPatientGoal = false;
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Actualiza el nombre y apellidos del paciente.
   */
  async onChangePatientName(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Actualizando el nombre del paciente ...'))));
      const data: UpdatePatientInformationRequestDTO = {
        name: this.user?.name ?? '',
        lastname: this.user?.lastname ?? '',
      };
      await firstValueFrom((this.store.dispatch(new PatientAction.Patch(this.clinicId, this.patientId, data))));
      const user = cloneDeep(
        this.storageService.getLocalItem<{
          token: string;
          user: IUser;
        }>('user')?.user
      );
      if (user && user.patients) {
        // significa que el paciente está registrado y quiere cambiar el nombre desde aquí
        user.name = data.name;
        user.lastname = data.lastname;
        await firstValueFrom((this.store.dispatch(new AuthActions.SetUser(user))));
      }
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    } finally {
      this.changePatientName = false;
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Envia a la información de mediciones del paciente
   */
  async onGoToMeasurements(): Promise<void> {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    await this.router.navigate(
      [
        'clinic',
        this.service?.clinicId,
        'patients',
        this.service?.servicePatients[0]?.patientId,
        'measurements',
      ],
      {
        queryParams: {
          ...queryParams
        },
      }
    );
  }

  /**
   * Envia a la información de diagnóstico del paciente
   */
  async onGoToDiagnostic(): Promise<void> {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    await this.router.navigate(
      [
        'clinic',
        this.service?.clinicId,
        'patients',
        this.service?.servicePatients[0]?.patientId,
        'diagnostic',
      ],
      {
        queryParams: {
          ...queryParams
        },
      }
    );
  }

  ngOnDestroy(): void {
    this.subscriptionPatient?.unsubscribe();
    this.servicePatientSubscription.unsubscribe();
    this.indicatorsPatientSubscription.unsubscribe();
  }
}
